import queryClient from "api/queryClient";
import { useStoreTenantUserMutation } from "api/TenantSlug/User/hooks.ts";
import { useUpdateUserSettingsMutation } from "api/TenantSlug/UserSetting/hooks.ts";
import { AddUserForm } from "components/Forms/AddUserForm/AddUserForm";
import { AddUserFormData } from "components/Forms/AddUserForm/formSchema";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser";
import { TOAST_TYPES } from "localConstants";
import { FC } from "react";
import { toast } from "react-toastify";
import { PermissionEnum, RoleEnum, UserSettingKeyEnum } from "types/enums.ts";
import { DrawerId } from "types/models";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";
import { showCustomToast } from "utils/toast";

interface ManageAdvertiserFormProps {
    onSuccess?: () => void;
    mode?: "create" | "detail";
    user?: App.Data.UserData;
}

export const ManageAdvertiserForm: FC<ManageAdvertiserFormProps> = ({ onSuccess, mode = "create", user }) => {
    const isCreateMode = mode === "create";

    const { t } = usePaldockTranslation("forms.advertiser-add");
    const { t: tCommon } = usePaldockTranslation("common");
    const userUtils = useUser();
    const permissionUtils = usePermission();
    const tenantSlug = userUtils.getActiveTenant()?.slug_name;

    const isSelf = userUtils.getAuthenticatedUser()?.id === user?.id;
    const canEditSettings = isSelf
        ? permissionUtils.hasPermission(PermissionEnum.TenantUserSettingsUpdate)
        : permissionUtils.hasPermission(PermissionEnum.TenantUserSettingsUpdateForeign);

    const registerUserMutation = useStoreTenantUserMutation({
        onSuccess: (response) => {
            showCustomToast(
                {
                    title: t("messages.success"),
                    buttons: [
                        {
                            label: tCommon("open"),
                            href:
                                response?.user?.id && tenantSlug
                                    ? `/${tenantSlug}/advertiser?drawerIds=${DrawerId.AdvertiserDetail}&${DrawerId.AdvertiserDetail}-advertiserId=${response.user.id}`
                                    : undefined,
                        },
                    ],
                },
                TOAST_TYPES.SUCCESS
            );

            queryClient.invalidateQueries({
                queryKey: ["users"],
            });
            queryClient.invalidateQueries({
                queryKey: ["getRoles"],
            });
            onSuccess?.();
        },
        onError: (error) => {
            toast.error(
                extractValidationErrorMessages(error)[0] ?? error.response?.data?.message ?? t("messages.error")
            );
        },
    });

    const updateUserSettingsMutation = useUpdateUserSettingsMutation(user?.id ?? "", {
        onSuccess: async () => {
            toast.success(t("messages.settings_saved"));
            await queryClient.invalidateQueries({ queryKey: ["getUser"] });
            if (isSelf) {
                await queryClient.invalidateQueries({ queryKey: ["getBalanceWallet"] });
                void userUtils.fetchUser();
            }
        },
        onError: (error) => {
            toast.error(
                extractValidationErrorMessages(error)[0] ?? error.response?.data?.message ?? t("messages.error")
            );
        },
    });

    // Disabling the fields is UI state only — react-hook-form still holds the prefetched values,
    // so the submit path must gate on the permission too, otherwise Save fires an unauthorized update.
    const userSettings = (formData: AddUserFormData) =>
        !canEditSettings
            ? []
            : [
                  ...(formData.country_id === undefined
                      ? []
                      : [{ key: UserSettingKeyEnum.Country, value: String(formData.country_id) }]),
                  ...(formData.currency_id === undefined
                      ? []
                      : [{ key: UserSettingKeyEnum.Currency, value: String(formData.currency_id) }]),
                  ...(formData.language_id === undefined
                      ? []
                      : [{ key: UserSettingKeyEnum.Language, value: String(formData.language_id) }]),
              ];

    const handleSubmit = (formData: AddUserFormData) => {
        if (isCreateMode) {
            if (!formData.password || !formData.password_confirmation) {
                return;
            }

            const settings = userSettings(formData);

            // Tenant-scoped create endpoint — no invitation email/activation step, the admin already
            // sets the password directly, so the account is created and added to the tenant immediately.
            registerUserMutation.mutate({
                email: formData.email,
                nickname: formData.nickname,
                role: RoleEnum.Advertiser,
                password: formData.password,
                password_confirmation: formData.password_confirmation,
                ...(settings.length > 0 ? { user_settings: settings } : {}),
            });
            return;
        }

        const settings = userSettings(formData);

        if (!user?.id || settings.length === 0) {
            return;
        }

        updateUserSettingsMutation.mutate({
            user_settings: settings,
        });
    };

    const countryIdFromSettings = user?.tenantSettingsKeyValues?.[UserSettingKeyEnum.Country];
    const currencyIdFromSettings = user?.tenantSettingsKeyValues?.[UserSettingKeyEnum.Currency];
    const languageIdFromSettings = user?.tenantSettingsKeyValues?.[UserSettingKeyEnum.Language];

    const disabledFields: Array<keyof AddUserFormData> = isCreateMode
        ? canEditSettings
            ? []
            : ["country_id", "currency_id", "language_id"]
        : canEditSettings
          ? ["nickname", "email"]
          : ["nickname", "email", "country_id", "currency_id", "language_id"];

    return (
        <AddUserForm
            key={`${mode}-${user?.id ?? "new"}`}
            handleSubmit={(formData) => handleSubmit(formData)}
            showPasswordFields={isCreateMode}
            preserveSubmittedValuesOnReset={!isCreateMode}
            initialValues={
                isCreateMode
                    ? undefined
                    : {
                          email: user?.email ?? "",
                          nickname: user?.nickname ?? "",
                          country_id: countryIdFromSettings ? Number(countryIdFromSettings) : undefined,
                          language_id: languageIdFromSettings ? Number(languageIdFromSettings) : undefined,
                          currency_id: currencyIdFromSettings ? Number(currencyIdFromSettings) : undefined,
                      }
            }
            disabledFields={disabledFields}
        />
    );
};
